import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import reportWebVitals from './reportWebVitals';
import { captureAttribution } from './lib/attribution';

// Components
import HeaderComponent from './components/headerComponent/HeaderComponent';
import Footer from './components/footer/Footer';
import HomePage from './HomePage';
import ProjectsPage from './components/projectsPage/ProjectsPage';
import ContactPage from './components/contactPage/ContactPage'
import ThankYouPage from './components/thankYouPage/ThankYouPage'
import CoachLandingPage from './components/coachLandingPage/CoachLandingPage';
import SalesforcePage from './components/salesforcePage/SalesforcePage';
import BusinessAutomationPage from './components/businessAutomationPage/BusinessAutomationPage';
import WebDevelopmentPage from './components/webDevelopmentPage/WebDevelopmentPage';
import PlatformIntegrationPage from './components/platformIntegrationPage/PlatformIntegrationPage';
import ConsultationPage from './components/consultationPage/ConsultationPage';
import DesignPage from './components/designPage/DesignPage';
import ToolsPage from './components/toolsPage/ToolsPage';
import QrToolPage from './components/qrToolPage/QrToolPage';
import TipSplitPage from './components/tipSplitPage/TipSplitPage';

// Make shared deep-links (e.g. /#start from a coupon or walk-in) scroll to the
// right section on load, which a single-page app doesn't do on its own.
const HashScroll = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [hash]);
  return null;
};

// True only while react-snap is crawling the app at build time (it sets a
// "ReactSnap" user agent). Used to skip things that shouldn't run during the
// prerender pass.
const isPrerender =
  typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);

const App = () => (
  <Router>
    <HeaderComponent />
    <HashScroll />
    <Routes>
      {/* Homepage (English + Spanish) */}
      <Route path="/" element={<HomePage />} />
      <Route path="/es" element={<HomePage />} />

      {/* Projects Page */}
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/coach-now" element={<CoachLandingPage />} />
      <Route path="/services/salesforce" element={<SalesforcePage />} />
      <Route path="/services/automation" element={<BusinessAutomationPage />} />
      <Route path="/services/web-development" element={<WebDevelopmentPage />} />
      <Route path="/services/integration" element={<PlatformIntegrationPage />} />
      <Route path="/services/consultation" element={<ConsultationPage />} />
      <Route path="/brand" element={<DesignPage />} />
      <Route path="/site-packages" element={<Navigate to="/brand" replace />} />

      {/* Free tools (lead magnets) */}
      <Route path="/tools" element={<ToolsPage />} />
      <Route path="/tools/qr" element={<QrToolPage />} />
      <Route path="/tools/qr-code-generator" element={<Navigate to="/tools/qr" replace />} />
      <Route path="/tools/tip-split" element={<TipSplitPage />} />
      <Route
        path="/tools/tip-split-calculator"
        element={<Navigate to="/tools/tip-split" replace />}
      />

    </Routes>
    <Footer />
    {/* Vercel Analytics injects <script src="/_vercel/insights/script.js">,
        which only exists on Vercel's edge at runtime — not in the build. During
        the react-snap prerender the local server returns HTML for it, which the
        browser tries to execute as JS ("Unexpected token '<'") and fails the
        crawl. Skip it while prerendering; real visitors still get it. */}
    {!isPrerender && <Analytics />}
  </Router>
);

// Capture campaign attribution — but not during the react-snap prerender
// (it's not a real visitor). The captured touch sticks to the lead on submit.
if (!isPrerender && typeof window !== 'undefined') {
  captureAttribution();
}

const rootElement = document.getElementById('root');
const tree = (
  <React.StrictMode>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// react-snap prerenders static HTML at build time; hydrate it if present,
// otherwise mount fresh (normal dev / non-prerendered loads).
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, tree);
} else {
  ReactDOM.createRoot(rootElement).render(tree);
}

reportWebVitals();
